import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ArrowLeft, Inbox, ChevronLeft, ChevronRight, Mail, Phone, Calendar, User } from "lucide-react";
import { Link } from "react-router-dom";

export default function Enquiries() {
  const [enquiries, setEnquiries] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const limit = 10;

  const fetchEnquiries = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get("/enquires", {
        params: { page, limit },
      });
      setEnquiries(response.data.enquiries || []);
      setTotalPages(response.data.pages || 1);
      setTotalItems(response.data.total || 0);
    } catch (err) {
      console.error("Error fetching enquiries:", err);
      setError("Failed to load enquiries. Please check if the API server is running.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEnquiries();
  }, [page]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      const date = new Date(dateStr);
      return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch (e) {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Breadcrumb / Back button */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs font-semibold text-sif-primary hover:underline"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>

        {/* Header section */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Inbox className="w-6 h-6 text-sif-primary" /> Enquiries Dashboard
            </h1>
            <p className="text-xs text-slate-500 font-medium mt-1">
              Hidden administrator route listing customer enquiry entries
            </p>
          </div>
          <div className="px-4 py-2 bg-slate-100 rounded-xl text-center sm:text-right">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Enquiries</span>
            <div className="text-2xl font-black text-sif-primary mt-0.5">{totalItems}</div>
          </div>
        </div>

        {/* Content Section */}
        {error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl text-sm font-medium">
            {error}
          </div>
        ) : loading ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-16 text-center text-slate-500 font-medium shadow-sm">
            <div className="w-8 h-8 border-4 border-sif-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            Loading enquiries...
          </div>
        ) : enquiries.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-16 text-center text-slate-400 font-medium shadow-sm flex flex-col items-center">
            <Inbox className="w-12 h-12 text-slate-300 mb-3" />
            <p className="text-sm font-semibold text-slate-700">No enquiries found</p>
            <p className="text-xs text-slate-500 mt-1">Submissions from the contact forms will appear here.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Desktop Table View */}
            <div className="hidden md:block bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    <th className="px-6 py-4">Received Date</th>
                    <th className="px-6 py-4">Full Name</th>
                    <th className="px-6 py-4">Email Address</th>
                    <th className="px-6 py-4">Mobile Number</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm text-slate-700">
                  {enquiries.map((enq, idx) => (
                    <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap font-medium text-slate-500 flex items-center gap-2">
                        <Calendar className="w-3.5 h-3.5 text-slate-400" />
                        {formatDate(enq.timestamp)}
                      </td>
                      <td className="px-6 py-4 font-semibold text-slate-900">{enq.fullName}</td>
                      <td className="px-6 py-4">
                        <a
                          href={`mailto:${enq.email}`}
                          className="inline-flex items-center gap-1.5 text-sif-primary hover:underline"
                        >
                          <Mail className="w-3.5 h-3.5 text-slate-400" />
                          {enq.email}
                        </a>
                      </td>
                      <td className="px-6 py-4">
                        <a
                          href={`tel:${enq.mobile}`}
                          className="inline-flex items-center gap-1.5 text-slate-800 hover:text-sif-primary transition-colors"
                        >
                          <Phone className="w-3.5 h-3.5 text-slate-400" />
                          {enq.mobile}
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card-based View */}
            <div className="grid grid-cols-1 gap-4 md:hidden">
              {enquiries.map((enq, idx) => (
                <div key={idx} className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm space-y-3.5">
                  <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5 text-slate-300" />
                      {formatDate(enq.timestamp)}
                    </span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2.5">
                      <User className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                      <div>
                        <span className="text-[10px] text-slate-400 uppercase font-semibold block">Full Name</span>
                        <span className="text-sm font-bold text-slate-900">{enq.fullName}</span>
                      </div>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <Mail className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                      <div>
                        <span className="text-[10px] text-slate-400 uppercase font-semibold block">Email</span>
                        <a href={`mailto:${enq.email}`} className="text-sm text-sif-primary hover:underline font-medium">
                          {enq.email}
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <Phone className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                      <div>
                        <span className="text-[10px] text-slate-400 uppercase font-semibold block">Mobile</span>
                        <a href={`tel:${enq.mobile}`} className="text-sm text-slate-800 font-medium">
                          {enq.mobile}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between bg-white px-4 py-3 sm:px-6 rounded-2xl border border-slate-200 shadow-sm">
                <div className="flex flex-1 justify-between sm:hidden">
                  <button
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                    className="relative inline-flex items-center rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 transition"
                  >
                    Previous
                  </button>
                  <button
                    disabled={page === totalPages}
                    onClick={() => setPage(page + 1)}
                    className="relative ml-3 inline-flex items-center rounded-lg border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 transition"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                  <div>
                    <p className="text-xs text-slate-500 font-medium">
                      Showing <span className="font-bold text-slate-900">{(page - 1) * limit + 1}</span> to{" "}
                      <span className="font-bold text-slate-900">{Math.min(page * limit, totalItems)}</span> of{" "}
                      <span className="font-bold text-slate-900">{totalItems}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="inline-flex -space-x-px rounded-lg shadow-2xs" aria-label="Pagination">
                      <button
                        disabled={page === 1}
                        onClick={() => setPage(page - 1)}
                        className="relative inline-flex items-center rounded-l-lg border border-slate-200 bg-white px-2.5 py-2 text-slate-500 hover:bg-slate-50 disabled:opacity-50 transition"
                      >
                        <span className="sr-only">Previous</span>
                        <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                      </button>
                      
                      {/* Page numbers */}
                      {Array.from({ length: totalPages }, (_, idx) => idx + 1).map((pNum) => (
                        <button
                          key={pNum}
                          onClick={() => setPage(pNum)}
                          aria-current={pNum === page ? "page" : undefined}
                          className={`relative inline-flex items-center px-4 py-2 text-xs font-semibold focus:z-20 transition ${
                            pNum === page
                              ? "z-10 bg-sif-primary text-white"
                              : "border-t border-b border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                          }`}
                        >
                          {pNum}
                        </button>
                      ))}

                      <button
                        disabled={page === totalPages}
                        onClick={() => setPage(page + 1)}
                        className="relative inline-flex items-center rounded-r-lg border border-slate-200 bg-white px-2.5 py-2 text-slate-500 hover:bg-slate-50 disabled:opacity-50 transition"
                      >
                        <span className="sr-only">Next</span>
                        <ChevronRight className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
